import { useEffect, useMemo, useState } from "react";
import Shell from "../components/Shell";
import { fetchGroups, updateGroup, deleteGroup, requestRescan } from "../lib/api";
import {
  TrashIcon,
  UsersThreeIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  PauseCircleIcon,
  GiftIcon,
  XIcon,
  ArrowsClockwiseIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "monitoring", label: "Monitoring" },
  { id: "paused", label: "Paused" },
];

export default function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [rescanning, setRescanning] = useState(false);

  const load = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      setGroups(await fetchGroups());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load(false), 10000);
    return () => clearInterval(t);
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groups.filter((g) => {
      if (filter === "monitoring" && !g.enabled) return false;
      if (filter === "paused" && g.enabled) return false;
      if (q && !(g.group_name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [groups, query, filter]);

  const monitoring = groups.filter((g) => g.enabled).length;

  const onRescan = async () => {
    setRescanning(true);
    try {
      await requestRescan();
      toast.success("Rescan requested — new groups appear within ~1 min");
      setTimeout(() => load(false), 15000);
      setTimeout(() => {
        load(false);
        setRescanning(false);
      }, 45000);
    } catch {
      toast.error("Could not request rescan");
      setRescanning(false);
    }
  };

  const onToggle = async (g) => {
    // Optimistic flip
    setGroups((prev) => prev.map((x) => (x.id === g.id ? { ...x, enabled: !x.enabled } : x)));
    try {
      const updated = await updateGroup(g.id, { enabled: !g.enabled });
      setGroups((prev) => prev.map((x) => (x.id === g.id ? updated : x)));
    } catch {
      // Revert
      setGroups((prev) => prev.map((x) => (x.id === g.id ? g : x)));
      toast.error("Update failed");
    }
  };

  const onHolidayToggle = async (g) => {
    const next = !g.holiday_greetings_enabled;
    setGroups((prev) => prev.map((x) => (x.id === g.id ? { ...x, holiday_greetings_enabled: next } : x)));
    try {
      const updated = await updateGroup(g.id, { holiday_greetings_enabled: next });
      setGroups((prev) => prev.map((x) => (x.id === g.id ? updated : x)));
      toast.success(next ? `Holiday greetings ON · ${g.group_name}` : `Holiday greetings off · ${g.group_name}`);
    } catch {
      setGroups((prev) => prev.map((x) => (x.id === g.id ? g : x)));
      toast.error("Update failed");
    }
  };

  const onDelete = async (g) => {
    if (!window.confirm(`Remove "${g.group_name}" from monitoring?`)) return;
    try {
      await deleteGroup(g.id);
      setGroups((prev) => prev.filter((x) => x.id !== g.id));
      toast.success(`Removed ${g.group_name}`);
    } catch {
      toast.error("Delete failed");
    }
  };

  const bulk = async (kind) => {
    let targets = [];
    let confirmMsg = "";
    if (kind === "pause-all") {
      targets = visible.filter((g) => g.enabled);
      confirmMsg = `Pause all ${targets.length} currently-monitored groups in this view?`;
    } else if (kind === "enable-all") {
      targets = visible.filter((g) => !g.enabled);
      confirmMsg = `Start monitoring all ${targets.length} paused groups in this view?`;
    } else if (kind === "delete-paused") {
      targets = visible.filter((g) => !g.enabled);
      confirmMsg = `Delete all ${targets.length} paused groups in this view? They will be re-discovered on next sync.`;
    }
    if (targets.length === 0) {
      toast.info("Nothing to update in this view");
      return;
    }
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      if (kind === "delete-paused") {
        await Promise.all(targets.map((g) => deleteGroup(g.id)));
        setGroups((prev) => prev.filter((g) => !targets.find((t) => t.id === g.id)));
        toast.success(`Deleted ${targets.length} group${targets.length === 1 ? "" : "s"}`);
      } else {
        const want = kind === "enable-all";
        await Promise.all(targets.map((g) => updateGroup(g.id, { enabled: want })));
        setGroups((prev) =>
          prev.map((g) => (targets.find((t) => t.id === g.id) ? { ...g, enabled: want } : g))
        );
        toast.success(`${want ? "Enabled" : "Paused"} ${targets.length} groups`);
      }
    } catch {
      toast.error("Bulk update failed");
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      title="Monitored groups"
      subtitle="Tap a row to toggle monitoring. New groups land paused — opt-in only."
    >
      {/* Counter + bulk actions */}
      <div
        data-testid="groups-summary"
        className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4"
      >
        <div className="flex items-baseline gap-2">
          <span className="font-heading text-3xl font-black tracking-tight">{monitoring}</span>
          <span className="text-sm text-zinc-500">of {groups.length} monitored</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="rescan-groups"
            onClick={onRescan}
            disabled={rescanning}
            title="Ask the listener to re-check WhatsApp for new groups"
            className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-zinc-950 bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-1.5"
          >
            <ArrowsClockwiseIcon size={13} weight="bold" className={rescanning ? "animate-spin" : ""} />
            {rescanning ? "Rescanning…" : "Rescan groups"}
          </button>
          <button
            data-testid="bulk-pause-all"
            onClick={() => bulk("pause-all")}
            disabled={busy}
            className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-zinc-300 bg-white hover:bg-zinc-50 disabled:opacity-50"
          >
            Pause all
          </button>
          <button
            data-testid="bulk-enable-all"
            onClick={() => bulk("enable-all")}
            disabled={busy}
            className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-zinc-300 bg-white hover:bg-zinc-50 disabled:opacity-50"
          >
            Enable all
          </button>
          <button
            data-testid="bulk-delete-paused"
            onClick={() => bulk("delete-paused")}
            disabled={busy}
            className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-zinc-300 bg-white hover:bg-zinc-50 disabled:opacity-50 text-[#FF3B30]"
          >
            Delete paused
          </button>
        </div>
      </div>

      {/* Search + filter chips */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <MagnifyingGlassIcon
            size={16}
            weight="bold"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <input
            data-testid="groups-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by group name…"
            className="w-full border border-zinc-300 pl-9 pr-9 py-2.5 text-sm focus:outline-none focus:border-zinc-950"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-950"
              aria-label="Clear"
              data-testid="groups-search-clear"
            >
              <XIcon size={14} weight="bold" />
            </button>
          )}
        </div>
        <div className="flex gap-1 border border-zinc-300 bg-white p-0.5">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`groups-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                filter === f.id ? "bg-zinc-950 text-white" : "text-zinc-500 hover:text-zinc-950"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Group list */}
      <div className="border border-zinc-200 bg-white" data-testid="groups-list">
        {loading && <div className="p-10 text-center text-sm text-zinc-400">Loading…</div>}
        {!loading && visible.length === 0 && (
          <div className="p-12 text-center">
            <UsersThreeIcon size={28} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-sm text-zinc-500">
              {groups.length === 0
                ? "No groups detected yet. Start the listener and post a message in any group."
                : "No groups match your filter."}
            </p>
          </div>
        )}
        {visible.map((g) => (
          <GroupRow
            key={g.id}
            group={g}
            onToggle={() => onToggle(g)}
            onDelete={() => onDelete(g)}
            onHolidayToggle={() => onHolidayToggle(g)}
          />
        ))}
      </div>
    </Shell>
  );
}

function GroupRow({ group, onToggle, onDelete, onHolidayToggle }) {
  const isOn = !!group.enabled;
  const holidayOn = !!group.holiday_greetings_enabled;
  return (
    <div
      data-testid={`group-row-${group.id}`}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      className={`flex items-center gap-3 px-4 py-3.5 border-b border-zinc-100 last:border-b-0 cursor-pointer transition-colors ${
        isOn ? "bg-emerald-50/40 hover:bg-emerald-50" : "hover:bg-zinc-50"
      }`}
    >
      {/* Status indicator */}
      <div
        className={`flex-shrink-0 w-9 h-9 flex items-center justify-center ${
          isOn ? "bg-emerald-500 text-white" : "bg-zinc-100 text-zinc-400"
        }`}
      >
        {isOn ? (
          <CheckCircleIcon size={20} weight="fill" />
        ) : (
          <PauseCircleIcon size={20} weight="fill" />
        )}
      </div>

      {/* Name + meta */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-zinc-950 truncate">{group.group_name}</div>
        <div className="flex items-center gap-3 text-[11px] text-zinc-500 mt-0.5">
          <span className="font-bold uppercase tracking-wider">
            {isOn ? (
              <span className="text-emerald-700">Monitoring</span>
            ) : (
              <span className="text-zinc-500">Paused</span>
            )}
          </span>
          <span>·</span>
          <span className="font-mono-num">{group.message_count ?? 0} msgs</span>
        </div>
      </div>

      {/* Toggle pill (still visible for clarity) */}
      <div
        data-testid={`group-toggle-${group.id}`}
        className={`relative inline-flex h-6 w-11 items-center transition-colors flex-shrink-0 ${
          isOn ? "bg-zinc-950" : "bg-zinc-300"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 bg-white transition-transform ${
            isOn ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </div>

      {/* Holiday greetings toggle */}
      <button
        data-testid={`group-holiday-${group.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onHolidayToggle();
        }}
        title={holidayOn ? "Holiday greetings ON — click to disable" : "Holiday greetings OFF — click to enable"}
        className={`flex-shrink-0 p-2 ${holidayOn ? "text-amber-500" : "text-zinc-300 hover:text-amber-400"}`}
        aria-label="Toggle holiday greetings"
      >
        <GiftIcon size={16} weight={holidayOn ? "fill" : "regular"} />
      </button>

      {/* Delete */}
      <button
        data-testid={`group-delete-${group.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="flex-shrink-0 p-2 -mr-2 text-zinc-300 hover:text-[#FF3B30]"
        aria-label="Remove group"
      >
        <TrashIcon size={16} weight="bold" />
      </button>
    </div>
  );
}
